import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import ConfirmDialog from '../common/ConfirmDialog';
import MultiSelectDropdown from '../common/MultiSelectDropdown';
import JobForm from '../documentation/JobForm';
import ShippingDetailsDialog from '../common/ShippingDetailsDialog';
import TableScrollContainer from '../common/TableScrollContainer';
import { openFilePreview } from '../common/FilePreviewModal';
import '../documentation/JobList.css';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';

const API_BASE = '/api/documentation/shipping-tasks-report';

const CopyCell = ({ text, children }) => {
    const [copied, setCopied] = React.useState(false);
    const handleCopy = (e) => {
        e.stopPropagation();
        const val = text != null ? String(text) : (typeof children === 'string' ? children : '');
        if (!val || val === '-') return;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(val).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
            });
        } else {
            const ta = document.createElement('textarea');
            ta.value = val;
            ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
            document.body.appendChild(ta);
            ta.focus();
            ta.select();
            try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch {}
            document.body.removeChild(ta);
        }
    };
    return (
        <span className="copy-cell">
            {children}
            <button
                type="button"
                className={`copy-icon-btn${copied ? ' copied' : ''}`}
                title={copied ? 'Copied!' : 'Copy'}
                onClick={handleCopy}
                onMouseDown={e => e.stopPropagation()}
            >
                {copied
                    ? <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    : <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
                }
            </button>
        </span>
    );
};

const dutyFlashStyleId = 'shipping-tasks-duty-flash-style';
if (!document.getElementById(dutyFlashStyleId)) {
    const styleEl = document.createElement('style');
    styleEl.id = dutyFlashStyleId;
    styleEl.innerHTML = `
    @keyframes strDutyPaidTextFlash {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.15; }
    }
    .str-duty-paid-flash { animation: strDutyPaidTextFlash 1s ease-in-out infinite; color: #dc3545; font-weight: 700; }
    .str-manifest-out-flash { animation: strDutyPaidTextFlash 1s ease-in-out infinite; color: #dc3545 !important; font-weight: 700; }
    @keyframes strBlCellFlash {
        0%   { background-color: #ffcccc; }
        50%  { background-color: #ff8080; }
        100% { background-color: #ffcccc; }
    }
    td.str-bl-flash { animation: strBlCellFlash 1.2s ease-in-out infinite; }
    tr.str-save-flash, tr.str-save-flash td { background-color: #fff3cd !important; }
    `;
    document.head.appendChild(styleEl);
}

const toUpper = (v) => (v || '').toString().toUpperCase();

const SortableRow = ({ id, disabled, style, title, className, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
    const mergedStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
        zIndex: isDragging ? 30 : undefined,
        ...style,
    };
    return (
        <tr ref={setNodeRef} style={mergedStyle} title={title} data-rowkey={id} className={className}>
            {typeof children === 'function' ? children({ listeners, attributes }) : children}
        </tr>
    );
};

const priorityCellStyle = (priority) => {
    const p = (priority || 'MEDIUM').toUpperCase();
    if (p === 'HIGH') return { backgroundColor: '#dc3545', color: '#fff' };
    if (p === 'MEDIUM') return { backgroundColor: '#fd7e14', color: '#fff' };
    return {};
};

const STAFF_CARD_COLORS = [
    { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
    { bg: '#dcfce7', border: '#86efac', text: '#166534' },
    { bg: '#fef9c3', border: '#fde047', text: '#854d0e' },
    { bg: '#fce7f3', border: '#f9a8d4', text: '#9d174d' },
    { bg: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' },
    { bg: '#ffedd5', border: '#fdba74', text: '#9a3412' },
    { bg: '#cffafe', border: '#67e8f9', text: '#155e75' },
    { bg: '#f0fdf4', border: '#4ade80', text: '#14532d' },
];

const fmtTime = (iso) => {
    if (!iso) return '-';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
};

const getDateInputValue = (d) => {
    if (!d) return '';
    if (typeof d === 'string') {
        const m = d.match(/^\d{4}-\d{2}-\d{2}/);
        return m ? m[0] : '';
    }
    try {
        const dt = new Date(d);
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, '0');
        const day = String(dt.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    } catch { return ''; }
};

const _fmtDate = (d, sep) => {
    if (!d) return '';
    try {
        const dt = new Date(d);
        if (isNaN(dt.getTime())) return d;
        const day = String(dt.getDate()).padStart(2, '0');
        const month = String(dt.getMonth() + 1).padStart(2, '0');
        const year = dt.getFullYear();
        return `${day}${sep}${month}${sep}${year}`;
    } catch { return d; }
};

const fmtDateDmy = (d) => _fmtDate(d, '-');
const fmtDateShort = (d) => _fmtDate(d, '/');

const getInitials = (name) => {
    if (!name) return '-';
    const parts = name.trim().split(/\s+/);
    if (!parts.length) return '-';
    return parts.map(p => p[0]?.toUpperCase() || '').join('');
};

const getTextColorForBackground = (hexColor) => {
    if (!hexColor || hexColor === '#FFFFFF') return '#000000';
    const rgb = parseInt(hexColor.slice(1), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = (rgb >> 0) & 0xff;
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luma < 128 ? '#FFFFFF' : '#000000';
};

const SortableShippingLineItem = ({ id, line, onColorChange }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px',
        margin: '0 0 8px 0',
        backgroundColor: line.color || '#FFFFFF',
        color: getTextColorForBackground(line.color || '#FFFFFF'),
        border: `1px solid ${line.color || '#eee'}`,
        borderRadius: '4px',
    };

    return (
        <div ref={setNodeRef} style={style}>
            <span {...attributes} {...listeners} style={{ flex: 1, cursor: 'grab' }}>
                {line.short_name || line.name}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                    type="color"
                    value={line.color || '#FFFFFF'}
                    onChange={(e) => onColorChange(e.target.value)}
                    title="Select Line Color"
                    style={{ width: '60px', height: '30px', padding: '2px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                />
            </div>
        </div>
    );
};

const SortableConfigRow = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px',
                borderBottom: '1px solid #eee',
                background: '#fff',
            }}
        >
            <span {...attributes} {...listeners} style={{ cursor: 'grab', color: '#adb5bd', touchAction: 'none' }} title="Drag to reorder">
                <i className="fa fa-grip-vertical" />
            </span>
            {children}
        </div>
    );
};

export default function ShippingTasksReport() {
    const { hasPermission } = usePermissions();
    const canCreate = hasPermission('SHIPPING TASKS REPORT', 'can_create');
    const canEdit = hasPermission('SHIPPING TASKS REPORT', 'can_edit');
    const canDelete = hasPermission('SHIPPING TASKS REPORT', 'can_delete');
    const canEditShippingReport = hasPermission('SHIPPING REPORTS', 'can_edit');
    const canDeleteShippingReport = hasPermission('SHIPPING REPORTS', 'can_delete');

    const [activeTab, setActiveTab] = useState('daily');
    const [reportDate, setReportDate] = useState(getDateInputValue(new Date()));
    const [doneFrom, setDoneFrom] = useState(() => getDateInputValue(new Date(Date.now() - 14 * 86400000)));
    const [doneTo, setDoneTo] = useState(getDateInputValue(new Date()));
    const [allFrom, setAllFrom] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return getDateInputValue(d); });
    const [allTo, setAllTo] = useState(getDateInputValue(new Date()));
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState({});
    const [editing, setEditing] = useState({});
    const [editingRows, setEditingRows] = useState({});
    const [rowOrderMap, setRowOrderMap] = useState({});
    const [cardFilter, setCardFilter] = useState('');
    const [slUserFilter, setSlUserFilter] = useState('');
    const [doneTasksModal, setDoneTasksModal] = useState(null);
    const [undoneTaskConfirm, setUndoneTaskConfirm] = useState(null);
    const [dropdowns, setDropdowns] = useState({ staff: [], remarks: [], tasks: [], shipping_lines: [] });
    const [shippingStaff, setShippingStaff] = useState([]);
    const [jobFormModal, setJobFormModal] = useState({ isOpen: false, job: null });
    const [shippingDetailsRow, setShippingDetailsRow] = useState(null);
    const [showConfig, setShowConfig] = useState(false);
    const [configTab, setConfigTab] = useState('remarks');
    const [remarks, setRemarks] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [shippingLines, setShippingLines] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [generated, setGenerated] = useState([]);
    const [dailyCount, setDailyCount] = useState(0);
    const [doneCount, setDoneCount] = useState(0);
    const [allCount, setAllCount] = useState(0);
    const [allStaffFilter, setAllStaffFilter] = useState('');
    const [allStatusFilter, setAllStatusFilter] = useState('');
    const [manifestFilter, setManifestFilter] = useState('');
    const [deleteReportConfirm, setDeleteReportConfirm] = useState(null);
    const [deleteTaskConfirm, setDeleteTaskConfirm] = useState(null);
    const [generatedCount, setGeneratedCount] = useState(0);
    const [taskModal, setTaskModal] = useState(null);
    const [savingOrder, setSavingOrder] = useState(false);
    const [addTaskDialog, setAddTaskDialog] = useState(null); // { value, options, activeIdx, job }
    const [scrollTarget, setScrollTarget] = useState(null);
    const [flashTaskId, setFlashTaskId] = useState(null); // last-saved task row highlight
    const [bulkMode, setBulkMode] = useState(null); // 'create' | 'edit' | null
    const [bulkSaving, setBulkSaving] = useState(false);

    const authHeaders = useCallback((json = true) => {
        const token = localStorage.getItem('kk_token');
        const headers = { Authorization: `Bearer ${token}` };
        if (json) headers['Content-Type'] = 'application/json';
        return headers;
    }, []);

    const openPdfPreview = async (url, fileName) => {
        const res = await fetch(url, { headers: authHeaders(false) });
        if (!res.ok) throw new Error('Failed to load PDF preview');
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const previewWindow = openFilePreview(blobUrl, fileName);
        if (previewWindow && previewWindow.addEventListener) {
            previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl), { once: true });
        } else {
            setTimeout(() => URL.revokeObjectURL(blobUrl), 5 * 60 * 1000);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchDropdowns = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/dropdowns/`, { headers: authHeaders(false) });
            if (!res.ok) throw new Error('Failed to load dropdowns');
            const data = await res.json();
            setDropdowns(data);
        } catch (err) {
            console.error(err);
        }
    }, [authHeaders]);

    const fetchData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const url = activeTab === 'done'
                ? `${API_BASE}/done/?date_from=${doneFrom}&date_to=${doneTo}`
                : activeTab === 'all'
                ? `${API_BASE}/all/?date_from=${allFrom}&date_to=${allTo}`
                : `${API_BASE}/daily/?report_date=${reportDate}`;
            const res = await fetch(url, { headers: authHeaders(false) });
            if (!res.ok) throw new Error('Failed to load report');
            const data = await res.json();
            if (activeTab === 'done' || activeTab === 'all') {
                setRows((data.rows || []).map(r => ({
                    ...r,
                    _clientKey: r.task_id ? `task-${r.task_id}` : `row-${r.id}`
                })));
                if (activeTab === 'done') {
                    setDoneCount(data.count ?? (data.rows || []).length);
                } else {
                    setAllCount(data.count ?? (data.rows || []).length);
                }
            } else {
                setRows((data.rows || []).map(j => ({
                    ...j,
                    tasks: (j.tasks || []).map((t, i) => ({
                        ...t,
                        _clientKey: t.task_id ? `task-${t.task_id}` : `placeholder-${j.id}-${i}`
                    }))
                })));
                setDailyCount(data.count ?? (data.rows || []).length);
                setRowOrderMap(data.row_order || {});
            }
            setEditing({});
            setEditingRows({});
            setBulkMode(null);
            setDoneTasksModal(null);
        } catch (err) {
            toast.error(err.message);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [activeTab, reportDate, doneFrom, doneTo, allFrom, allTo, authHeaders]);

    const fetchGenerated = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/generated/`, { headers: authHeaders(false) });
            if (!res.ok) throw new Error('Failed to load generated reports');
            const data = await res.json();
            setGenerated(data || []);
            setGeneratedCount(data.length);
        } catch (err) {
            toast.error(err.message);
        }
    }, [authHeaders]);

    const deleteJobTask = async (row) => {
        try {
            const res = await fetch(`${API_BASE}/job-tasks/${row.task_id}/`, { method: 'DELETE', headers: authHeaders(false) });
            if (!res.ok && res.status !== 204) throw new Error('Failed to delete task');
            toast.success('Task deleted');
            setRows(prev => prev
                .map(r => Array.isArray(r.tasks)
                    ? { ...r, tasks: r.tasks.filter(t => t.task_id !== row.task_id) }
                    : r)
                .filter(r => Array.isArray(r.tasks) || r.task_id !== row.task_id)
            );
        } catch (err) {
            toast.error(err.message);
        }
    };

    const deleteGeneratedReport = async (r) => {
        try {
            const res = await fetch(`${API_BASE}/generated/${r.id}/`, { method: 'DELETE', headers: authHeaders(false) });
            if (!res.ok && res.status !== 204) throw new Error('Failed to delete report');
            toast.success('Report deleted');
            setGenerated(prev => prev.filter(g => g.id !== r.id));
            setGeneratedCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            toast.error(err.message);
        }
    };

    const fetchConfig = useCallback(async () => {
        try {
            const [rRes, lRes, tRes] = await Promise.all([
                fetch(`${API_BASE}/remarks/`, { headers: authHeaders(false) }),
                fetch(`${API_BASE}/line-ordering/`, { headers: authHeaders(false) }),
                fetch(`${API_BASE}/tasks/`, { headers: authHeaders(false) }),
            ]);
            if (rRes.ok) setRemarks(await rRes.json());
            if (tRes.ok) setTasks(await tRes.json());
            if (lRes.ok) {
                const lines = await lRes.json();
                setShippingLines(Array.isArray(lines) && lines.length ? lines : (dropdowns.shipping_lines || []));
            } else {
                setShippingLines(dropdowns.shipping_lines || []);
            }
        } catch (err) {
            console.error(err);
            setShippingLines(dropdowns.shipping_lines || []);
        }
    }, [authHeaders, dropdowns]);

    useEffect(() => { fetchDropdowns(); }, [fetchDropdowns]);

    // SL Users for the filter — same source/rules as Shipping Report:
    // shipping-department staff that have a linked user account.
    useEffect(() => {
        fetch('/api/admin/staff/?active=true', { headers: authHeaders(false) })
            .then(res => (res.ok ? res.json() : []))
            .then(data => {
                const filtered = (Array.isArray(data) ? data : []).filter(s =>
                    (s.department_name || '').toUpperCase().includes('SHIPPING') && s.user_id != null
                );
                setShippingStaff(filtered);
            })
            .catch(() => {});
    }, [authHeaders]);

    useEffect(() => {
        if (activeTab === 'daily' || activeTab === 'done' || activeTab === 'all') {
            fetchData();
        } else if (activeTab === 'generated') {
            fetchGenerated();
        }
    }, [activeTab, reportDate, fetchData, fetchGenerated]);

    useEffect(() => {
        if (showConfig) fetchConfig();
    }, [showConfig, fetchConfig]);

    // Debounced DO-RECEIVED job search for the + (add task) dialog
    useEffect(() => {
        if (!addTaskDialog) return undefined;
        const term = (addTaskDialog.value || '').trim();
        if (addTaskDialog.job || term.length < 2) {
            if (!addTaskDialog.job && (addTaskDialog.options || []).length) {
                setAddTaskDialog(prev => (prev ? { ...prev, options: [] } : prev));
            }
            return undefined;
        }
        const t = setTimeout(async () => {
            try {
                const res = await fetch(`${API_BASE}/do-received-jobs/?q=${encodeURIComponent(term)}`, { headers: authHeaders(false) });
                const data = res.ok ? await res.json() : [];
                setAddTaskDialog(prev => (prev && !prev.job ? { ...prev, options: data, activeIdx: -1 } : prev));
            } catch {
                setAddTaskDialog(prev => (prev ? { ...prev, options: [] } : prev));
            }
        }, 300);
        return () => clearTimeout(t);
    }, [addTaskDialog, authHeaders]);

    // Clear the saved-row highlight after 5s
    useEffect(() => {
        if (!flashTaskId) return;
        const t = setTimeout(() => setFlashTaskId(null), 5000);
        return () => clearTimeout(t);
    }, [flashTaskId]);

    // Scroll to a newly added row once it has rendered
    useEffect(() => {
        if (!scrollTarget) return;
        const esc = window.CSS && window.CSS.escape ? window.CSS.escape(scrollTarget) : scrollTarget;
        const el = document.querySelector(`[data-rowkey="${esc}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setScrollTarget(null);
        }
    }, [scrollTarget, rows]);

    const lineOrder = useMemo(() => {
        const map = {};
        (dropdowns.shipping_lines || []).forEach(l => {
            map[(l.short_name || l.name || '').toString().trim().toUpperCase()] = l.order ?? 999999;
        });
        return map;
    }, [dropdowns.shipping_lines]);

    const searchedRows = useMemo(() => {
        const term = search.trim().toUpperCase();
        let result = rows;
        if (term) {
            if (activeTab === 'done' || activeTab === 'all') {
                result = rows.filter(r =>
                    toUpper(r.id).includes(term) ||
                    toUpper(r.idf_no).includes(term) ||
                    toUpper(r.bl).includes(term) ||
                    toUpper(r.customer_name).includes(term) ||
                    toUpper(r.shipping_line).includes(term) ||
                    toUpper(r.vessel).includes(term) ||
                    toUpper(r.task).includes(term) ||
                    toUpper(r.remarks).includes(term) ||
                    toUpper(r.staff_short_name || r.staff_name).includes(term)
                );
            } else {
                result = rows.filter(j =>
                    toUpper(j.id).includes(term) ||
                    toUpper(j.idf_no).includes(term) ||
                    toUpper(j.bl).includes(term) ||
                    toUpper(j.customer_name).includes(term) ||
                    toUpper(j.shipping_line).includes(term) ||
                    toUpper(j.vessel).includes(term) ||
                    (j.tasks || []).some(t => toUpper(t.task).includes(term) || toUpper(t.remarks).includes(term))
                );
            }
        }
        if (slUserFilter === '__NONE__') {
            result = result.filter(r => !(r.sl_user || '').trim());
        } else if (slUserFilter) {
            result = result.filter(r => (r.sl_user || '') === slUserFilter);
        }
        if (manifestFilter === 'out') {
            result = result.filter(r => (r.manifest_out_date || '').trim());
        } else if (manifestFilter === 'pending') {
            result = result.filter(r => !(r.manifest_out_date || '').trim());
        }
        if (activeTab === 'all') {
            if (allStaffFilter === '__NONE__') {
                result = result.filter(r => !(r.staff_short_name || r.staff_name || '').trim());
            } else if (allStaffFilter) {
                result = result.filter(r => ((r.staff_short_name || r.staff_name || '').trim()) === allStaffFilter);
            }
            if (allStatusFilter === 'done') {
                result = result.filter(r => r.done);
            } else if (allStatusFilter === 'pending') {
                result = result.filter(r => !r.done);
            }
        }
        const slKey = (line) => (line || '').toString().trim().toUpperCase();
        return [...result].sort((a, b) => {
            const aOrd = lineOrder[slKey(a.shipping_line)] ?? 999999;
            const bOrd = lineOrder[slKey(b.shipping_line)] ?? 999999;
            return aOrd - bOrd;
        });
    }, [rows, search, activeTab, lineOrder, slUserFilter, allStaffFilter, allStatusFilter, manifestFilter]);

    const filteredRows = useMemo(() => {
        if (activeTab !== 'daily' || !cardFilter) return searchedRows;
        if (cardFilter === 'TASKS') {
            return searchedRows.filter(j => (j.tasks || []).some(t => (t.task || '').trim() || !t.task_id));
        }
        return searchedRows.filter(j => (j.tasks || []).some(t =>
            (t.task || '').trim() &&
            (((t.staff_short_name || t.staff_name || '').trim()) || 'UNASSIGNED') === cardFilter
        ));
    }, [searchedRows, activeTab, cardFilter]);

    const dailyCards = useMemo(() => {
        if (activeTab !== 'daily') return null;
        const map = new Map();
        let tasksOnly = 0;
        searchedRows.forEach(j => {
            const staffs = new Set();
            (j.tasks || []).forEach(t => {
                if (!(t.task || '').trim()) return;
                staffs.add((t.staff_short_name || t.staff_name || '').trim() || 'UNASSIGNED');
            });
            if (staffs.size) tasksOnly += 1;
            staffs.forEach(s => map.set(s, (map.get(s) || 0) + 1));
        });
        const staff = Array.from(map.entries())
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => (a.name === 'UNASSIGNED' ? 1 : b.name === 'UNASSIGNED' ? -1 : a.name.localeCompare(b.name)));
        return { all: searchedRows.length, tasksOnly, staff };
    }, [searchedRows, activeTab]);

    // Staff options for the All tab — built from the names actually on tasks,
    // so ad-hoc free-text assignees are included too.
    const allStaffOptions = useMemo(() => {
        const names = new Set();
        rows.forEach(r => {
            const n = (r.staff_short_name || r.staff_name || '').trim();
            if (n) names.add(n);
        });
        return [
            { id: '', text: 'All Staff' },
            { id: '__NONE__', text: 'Not Assigned' },
            ...Array.from(names).sort().map(n => ({ id: n, text: n })),
        ];
    }, [rows]);

    // Scroll-lock: first 4 columns stay pinned while the table scrolls
    // horizontally. Column order differs per tab, so widths/offsets are
    // per-table constants.
    const DAILY_STICKY = [{ left: 0, w: 60 }, { left: 60, w: 110 }, { left: 170, w: 170 }, { left: 340, w: 140 }];
    const LIST_STICKY = [{ left: 0, w: 60 }, { left: 60, w: 110 }, { left: 170, w: 140 }, { left: 310, w: 170 }];
    const stickyTh = (c, extra = {}) => ({ position: 'sticky', left: c.left, zIndex: 20, width: c.w, minWidth: c.w, ...extra });
    const stickyTd = (c, bg = '#fff', extra = {}) => ({ position: 'sticky', left: c.left, zIndex: 10, width: c.w, minWidth: c.w, backgroundColor: bg, ...extra });

    // IDF wraps "(TASAC)" onto its own line; Date In shows underneath.
    const renderIdfCell = (row) => {
        const idf = toUpper(row.idf_no);
        const idx = idf.indexOf('(TASAC)');
        return (
            <CopyCell text={`${idf} ${row.date_in || ''}`.trim()}>
                {idx >= 0 ? (
                    <>
                        <div>{idf.slice(0, idx).trim()}</div>
                        <div>{idf.slice(idx).trim()}</div>
                    </>
                ) : (
                    <div>{idf}</div>
                )}
                {row.date_in && (
                    <div style={{ fontSize: 11, color: '#6c757d' }}>{fmtDateShort(row.date_in)}</div>
                )}
            </CopyCell>
        );
    };

    const getDoneRowKey = (row) => row._clientKey || (row.task_id ? `task-${row.task_id}` : `done-${row.id}`);
    const getTaskKey = (task, jobId) => task._clientKey || (task.task_id ? `task-${task.task_id}` : `new-${jobId}-${Date.now()}`);

    const renderManifestOut = (row) => {
        const date = row.manifest_out_date;
        if (!date) return null;
        const acked = !!row.shipping_acknowledge_manifest;
        return (
            <i
                className={`fa fa-check${acked ? '' : ' str-manifest-out-flash'}`}
                title={acked
                    ? `Manifest Out: ${fmtDateDmy(date)} — acknowledged${row.shipping_acknowledge_manifest_by ? ` by ${row.shipping_acknowledge_manifest_by}` : ''}`
                    : `Manifest Out: ${fmtDateDmy(date)}${canEdit ? ' — double-click to acknowledge' : ''}`}
                onDoubleClick={!acked && canEdit ? () => acknowledgeManifestOut(row) : undefined}
                style={{ color: '#28a745', fontSize: 16, cursor: !acked && canEdit ? 'pointer' : 'default' }}
            />
        );
    };

    const renderEtaEtbAtb = (row) => (
        <span style={{ whiteSpace: 'pre-line' }}>
            {fmtDateShort(row.eta) || '-'}{'\n'}
            {fmtDateShort(row.etb) || '-'}{'\n'}
            {fmtDateShort(row.atb) || '-'}
        </span>
    );

    const renderIcdNomination = (row) => {
        const nominated = (row.icd_nomination || '').trim().toUpperCase();
        const actual = (row.icd || '').trim().toUpperCase();
        const status = (row.icd_nomination_status || '').trim().toUpperCase();
        let icon = 'fa-question';
        let color = '#6c757d';
        let title = status || 'PENDING';
        if (nominated && actual && nominated === actual) {
            icon = 'fa-check-double';
            color = '#28a745';
            title = 'NOMINATED ICD MATCHES ICD';
        } else if (status === 'ACCEPTED' || (nominated && status !== 'PENDING' && status !== 'REJECTED')) {
            icon = 'fa-check';
            color = '#007bff';
            title = status || 'ACCEPTED';
        } else if (status === 'PENDING' || (!status && !nominated)) {
            icon = 'fa-hourglass-half';
            color = '#fd7e14';
            title = 'PENDING';
        } else if (status === 'REJECTED') {
            icon = 'fa-times';
            color = '#dc3545';
            title = 'REJECTED';
        }
        return <i className={`fa ${icon}`} title={title} style={{ color, fontSize: 16 }} />;
    };

    const renderShippingLine = (row) => <span>{row.shipping_line_short_name || row.shipping_line_full_name || row.shipping_line || '-'}</span>;
    const renderCustomer = (row) => {
        if (row.customer_short_name) return <span>{row.customer_short_name}</span>;
        return <strong>{row.customer_full_name || row.customer_name || '-'}</strong>;
    };
    const renderDone = (done) => <i className={`fa ${done ? 'fa-check' : 'fa-times'}`} style={{ color: done ? '#28a745' : '#dc3545', fontSize: 16 }} />;

    const acknowledgeManifestOut = async (row) => {
        const jobId = row.job_id || row.id;
        if (!jobId) return;
        try {
            const res = await fetch(`${API_BASE}/acknowledge-manifest/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ job_id: jobId }),
            });
            if (!res.ok) throw new Error('Failed to acknowledge manifest out');
            setRows(prev => prev.map(r => ((r.job_id || r.id) === jobId ? { ...r, shipping_acknowledge_manifest: true } : r)));
        } catch (err) {
            toast.error(err.message);
        }
    };

    const acknowledgeDutyPaid = async (row) => {
        const jobId = row.job_id || row.id;
        if (!jobId) return;
        try {
            const res = await fetch(`${API_BASE}/acknowledge-duty/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ job_id: jobId }),
            });
            if (!res.ok) throw new Error('Failed to acknowledge duty paid');
            setRows(prev => prev.map(r => ((r.job_id || r.id) === jobId ? { ...r, shipping_acknowledge_duty: true } : r)));
        } catch (err) {
            toast.error(err.message);
        }
    };

    const renderContainerSize = (row) => {
        const sizes = (row.container_size || '').split(',').map(s => s.trim()).filter(Boolean);
        if (!sizes.length) return '-';
        return sizes.map((s, i) => <div key={i}>{toUpper(s)}</div>);
    };

    const tableRows = useMemo(() => {
        if (activeTab !== 'daily') return filteredRows;
        const flat = [];
        filteredRows.forEach(job => {
            const tasks = job.tasks || [];
            if (!tasks.length) {
                flat.push({ ...job, _clientKey: `job-${job.id}` });
            } else {
                tasks.forEach(task => {
                    flat.push({ ...job, ...task, _clientKey: task._clientKey || `new-${job.id}-${Date.now()}` });
                });
            }
        });
        return flat;
    }, [filteredRows, activeTab]);

    const dailyGroups = useMemo(() => {
        if (activeTab !== 'daily') return [];
        const groups = [];
        const byKey = new Map();
        tableRows.forEach(r => {
            const key = String(r.shipping_line_id ?? 'none');
            if (!byKey.has(key)) {
                const g = { key, rows: [] };
                byKey.set(key, g);
                groups.push(g);
            }
            byKey.get(key).rows.push(r);
        });
        const manifestTs = (v) => {
            if (!v) return null;
            const t = Date.parse(String(v).replace(' ', 'T'));
            return Number.isNaN(t) ? null : t;
        };
        groups.forEach(g => {
            // Default order within a shipping line: manifest date ascending,
            // rows without a manifest date go to the bottom.
            g.rows.sort((a, b) => {
                const ta = manifestTs(a.manifest_out_date);
                const tb = manifestTs(b.manifest_out_date);
                if (ta !== null && tb !== null) return ta - tb;
                if (ta !== null) return -1;
                if (tb !== null) return 1;
                return 0;
            });
            const order = rowOrderMap[g.key];
            if (!order || !order.length) return;
            const idx = new Map(order.map((k, i) => [k, i]));
            g.rows = [...g.rows].sort((a, b) => {
                const ai = idx.has(a._clientKey) ? idx.get(a._clientKey) : order.length;
                const bi = idx.has(b._clientKey) ? idx.get(b._clientKey) : order.length;
                return ai - bi;
            });
        });
        return groups;
    }, [tableRows, rowOrderMap, activeTab]);

    const handleDailyDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const lineKeyOf = (row) => String(row.shipping_line_id ?? 'none');
        const rowByKey = new Map(tableRows.map(r => [r._clientKey, r]));
        const activeRow = rowByKey.get(active.id);
        const overRow = rowByKey.get(over.id);
        if (!activeRow || !overRow) return;
        const lineKey = lineKeyOf(activeRow);
        if (lineKey !== lineKeyOf(overRow)) return;
        const group = dailyGroups.find(g => g.key === lineKey);
        if (!group) return;
        const keys = group.rows.map(r => r._clientKey);
        const next = arrayMove(keys, keys.indexOf(active.id), keys.indexOf(over.id));
        setRowOrderMap(prev => ({ ...prev, [lineKey]: next }));
        fetch(`${API_BASE}/reorder/`, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({
                report_date: reportDate,
                shipping_line_id: lineKey === 'none' ? null : Number(lineKey),
                row_keys: next,
            })
        }).then(res => {
            if (!res.ok) throw new Error('Failed to save row order');
        }).catch(err => toast.error(err.message));
    };

    const doneJobRows = useMemo(() => {
        if (activeTab !== 'done') return [];
        const map = new Map();
        filteredRows.forEach(r => {
            const key = r.id;
            if (!map.has(key)) map.set(key, { jobRow: r, tasks: [] });
            map.get(key).tasks.push(r);
        });
        return Array.from(map.values());
    }, [filteredRows, activeTab]);

    const generatedByDate = useMemo(() => {
        const map = new Map();
        generated.forEach(r => {
            if (!map.has(r.report_date)) map.set(r.report_date, []);
            map.get(r.report_date).push(r);
        });
        return Array.from(map.entries());
    }, [generated]);

    const handleTaskEdit = (task, jobId, field, value) => {
        const key = getTaskKey(task, jobId);
        setEditing(prev => ({
            ...prev,
            [key]: { ...(prev[key] || {}), [field]: value }
        }));
    };

    const getTaskEdit = (task, jobId, field) => {
        const ed = editing[getTaskKey(task, jobId)] || {};
        if (ed[field] !== undefined) return ed[field];
        if (field === 'staff_id') return task.staff_id || (task.staff_name ? `new-staff:${task.staff_name}` : '');
        if (field === 'task') return task.task || '';
        if (field === 'done') return task.done || false;
        if (field === 'remarks') return task.remarks || '';
        if (field === 'priority') return task.priority || 'MEDIUM';
        return '';
    };

    const addTaskToJob = (job) => {
        const newKey = `new-${job.id}-${Date.now()}`;
        const newTask = {
            task_id: null,
            staff_id: '',
            staff_name: '',
            task: '',
            done: false,
            remarks: '',
            _clientKey: newKey,
        };
        setRows(prev => prev.map(j =>
            j.id === job.id ? { ...j, tasks: [...(j.tasks || []), newTask] } : j
        ));
        setEditingRows(prev => ({ ...prev, [newKey]: 'create' }));
    };

    // Add a task row for a job that is not in the daily list (e.g. DO RECEIVED)
    const createTaskRow = (job) => {
        const newKey = `new-${job.id}-${Date.now()}`;
        const newTask = {
            task_id: null,
            staff_id: '',
            staff_name: '',
            task: '',
            done: false,
            remarks: '',
            _clientKey: newKey,
        };
        setRows(prev => {
            if (prev.some(j => j.id === job.id)) {
                return prev.map(j =>
                    j.id === job.id ? { ...j, tasks: [...(j.tasks || []), newTask] } : j
                );
            }
            return [...prev, { ...job, tasks: [newTask], pending_receipt: false, _addedJob: true }];
        });
        setEditingRows(prev => ({ ...prev, [newKey]: 'create' }));
        setAddTaskDialog(null);
        setScrollTarget(newKey);
    };

    const startTaskEdit = (row) => {
        setEditingRows(prev => ({ ...prev, [row._clientKey]: 'edit' }));
    };

    const cancelTaskEdit = (row) => {
        const key = row._clientKey;
        if (editingRows[key] === 'create') {
            setRows(prev => prev
                .map(j =>
                    j.id === row.id
                        ? { ...j, tasks: (j.tasks || []).filter(t => (t._clientKey || '') !== key) }
                        : j
                )
                // Drop job rows added via the + dialog if their new task is cancelled
                .filter(j => !(j._addedJob && !(j.tasks || []).length))
            );
        }
        setEditingRows(prev => { const next = { ...prev }; delete next[key]; return next; });
        setEditing(prev => { const next = { ...prev }; delete next[key]; return next; });
    };

    // ---- Bulk create / edit -------------------------------------------
    // Bulk Create: add one editable (unsaved) task row to every job
    // currently visible in the daily table.
    const startBulkCreate = () => {
        const visibleIds = new Set(filteredRows.map(j => j.id));
        const stamp = Date.now();
        const newKeys = [];
        setRows(prev => prev.map(j => {
            if (!visibleIds.has(j.id)) return j;
            const key = `new-${j.id}-bulk-${stamp}`;
            newKeys.push(key);
            return {
                ...j,
                tasks: [...(j.tasks || []), {
                    task_id: null,
                    staff_id: '',
                    staff_name: '',
                    task: '',
                    done: false,
                    remarks: '',
                    priority: 'MEDIUM',
                    _clientKey: key,
                }],
            };
        }));
        setEditingRows(prev => {
            const next = { ...prev };
            newKeys.forEach(k => { next[k] = 'create'; });
            return next;
        });
        setBulkMode('create');
    };

    // Bulk Edit: put every visible saved task row into edit mode.
    const startBulkEdit = () => {
        const keys = {};
        tableRows.forEach(r => {
            if (r.task_id) keys[r._clientKey] = 'edit';
        });
        if (!Object.keys(keys).length) {
            toast.warn('No tasks to edit.');
            return;
        }
        setEditingRows(prev => ({ ...prev, ...keys }));
        setBulkMode('edit');
    };

    // Abort a bulk operation: drop unsaved new rows and all edit state.
    const cancelBulk = () => {
        setRows(prev => prev
            .map(j => ({
                ...j,
                tasks: (j.tasks || []).filter(t => editingRows[t._clientKey] !== 'create'),
            }))
            .filter(j => !(j._addedJob && !(j.tasks || []).length))
        );
        setEditing({});
        setEditingRows({});
        setBulkMode(null);
    };

    // Save every row currently in create/edit mode with one request.
    // Rows with no task selected are skipped — never saved.
    const handleBulkSave = async () => {
        const items = [];
        let skipped = 0;
        tableRows.forEach(row => {
            const key = row._clientKey;
            if (!editingRows[key]) return;
            const ed = editing[key] || {};
            let staff_id = getTaskEdit(row, row.id, 'staff_id');
            let staff_name = '';
            if (typeof staff_id === 'string' && staff_id.startsWith('new-staff:')) {
                staff_name = staff_id.slice('new-staff:'.length).trim();
                staff_id = '';
            }
            const taskText = (ed.task !== undefined ? ed.task : (row.task || '')).toString().trim();
            if (!taskText) {
                skipped += 1;
                return;
            }
            items.push({
                task_id: row.task_id || null,
                job_id: row.id,
                staff_id,
                staff_name,
                task: taskText,
                done: ed.done !== undefined ? ed.done : (row.done || false),
                remarks: ed.remarks !== undefined ? ed.remarks : (row.remarks || ''),
                priority: ed.priority !== undefined ? ed.priority : (row.priority || 'MEDIUM'),
            });
        });
        if (!items.length) {
            toast.warn('Nothing to save — select a task first.');
            return;
        }
        setBulkSaving(true);
        try {
            const res = await fetch(`${API_BASE}/bulk-save-tasks/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ report_date: reportDate, tasks: items }),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                throw new Error(e.detail || 'Failed to save tasks');
            }
            const data = await res.json();
            toast.success(`${data.saved ?? items.length} task(s) saved${skipped ? `, ${skipped} skipped (no task selected)` : ''}.`);
            setBulkMode(null);
            setEditing({});
            setEditingRows({});
            await fetchData(true);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBulkSaving(false);
        }
    };

    const saveTask = async (job, task) => {
        const key = getTaskKey(task, job.id);
        const ed = editing[key] || {};
        let staff_id = getTaskEdit(task, job.id, 'staff_id');
        let staff_name = '';
        if (typeof staff_id === 'string' && staff_id.startsWith('new-staff:')) {
            staff_name = staff_id.slice('new-staff:'.length).trim();
            staff_id = '';
        }
        const taskText = (ed.task !== undefined ? ed.task : (task.task || '')).toString();
        const done = ed.done !== undefined ? ed.done : (task.done || false);
        const remarks = ed.remarks !== undefined ? ed.remarks : (task.remarks || '');
        const priority = ed.priority !== undefined ? ed.priority : (task.priority || 'MEDIUM');

        if (!task.task_id && !taskText.trim()) {
            toast.warn('Task is required.');
            return;
        }

        setSaving(prev => ({ ...prev, [key]: true }));
        try {
            const res = await fetch(`${API_BASE}/save-task/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    task_id: task.task_id || null,
                    job_id: job.id,
                    report_date: reportDate,
                    staff_id,
                    staff_name,
                    task: taskText,
                    done,
                    remarks,
                    priority,
                })
            });
            if (!res.ok) throw new Error('Failed to save task');
            const saved = await res.json().catch(() => null);
            toast.success('Task saved.');
            setEditing(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            setEditingRows(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            if (saved && saved.id) setFlashTaskId(saved.id);
            await fetchData(true);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSaving(prev => ({ ...prev, [key]: false }));
        }
    };

    const [generateConfirm, setGenerateConfirm] = useState(false);

    const generateReport = async (confirm = false) => {
        setGenerating(true);
        try {
            const res = await fetch(`${API_BASE}/generate/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ report_date: reportDate, confirm })
            });
            if (!res.ok) throw new Error('Failed to generate report');
            const data = await res.json();
            if (data.requires_confirmation) {
                setGenerateConfirm(true);
                return;
            }
            toast.success(`Generated ${data.title || data.report_date}`);
            setActiveTab('generated');
            fetchGenerated();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setGenerating(false);
        }
    };

    const openPreview = async () => {
        try {
            await openPdfPreview(
                `${API_BASE}/preview-pdf/?report_date=${reportDate}`,
                `Daily_Shipping_Tasks_${reportDate}.pdf`
            );
        } catch (err) {
            toast.error(err.message);
        }
    };

    const openReportView = async (r) => {
        try {
            await openPdfPreview(
                `${API_BASE}/generated/${r.id}/pdf/`,
                `Daily_Shipping_Tasks_${r.report_date}_${r.id}.pdf`
            );
        } catch (err) {
            toast.error(err.message);
        }
    };

    const downloadGenerated = async (r, type) => {
        try {
            const res = await fetch(`${API_BASE}/generated/${r.id}/${type}/`, { headers: authHeaders(false) });
            if (!res.ok) throw new Error('Failed to download report');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Daily_Shipping_Tasks_${r.report_date}_${r.id}.${type === 'pdf' ? 'pdf' : 'xlsx'}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const saveRemark = async (remark) => {
        try {
            const res = await fetch(`${API_BASE}/remarks/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    text: (remark.text || '').trim().toUpperCase(),
                    color: remark.color || '#FFFFFF',
                    order: remarks.length,
                })
            });
            if (!res.ok) throw new Error('Failed to save remark');
            toast.success('Remark saved.');
            fetchConfig();
        } catch (err) { toast.error(err.message); }
    };

    const deleteRemark = async (id) => {
        if (!window.confirm('Delete this remark?')) return;
        try {
            const res = await fetch(`${API_BASE}/remarks/${id}/`, {
                method: 'DELETE',
                headers: authHeaders(false)
            });
            if (!res.ok) throw new Error('Failed to delete');
            toast.success('Remark deleted.');
            fetchConfig();
        } catch (err) { toast.error(err.message); }
    };

    const saveLineOrder = async (line) => {
        try {
            const res = await fetch(`${API_BASE}/line-ordering/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    shipping_line_id: line.shipping_line_id || line.id,
                    order: parseInt(line.order || 0),
                    color: line.color || '#FFFFFF',
                })
            });
            if (!res.ok) throw new Error('Failed to save order');
            toast.success('Order saved.');
            fetchConfig();
        } catch (err) { toast.error(err.message); }
    };

    const updateRemark = async (remark, id) => {
        try {
            const res = await fetch(`${API_BASE}/remarks/${id}/`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({
                    text: (remark.text || '').trim().toUpperCase(),
                    color: remark.color || '#FFFFFF',
                })
            });
            if (!res.ok) throw new Error('Failed to update remark');
            toast.success('Remark updated.');
            setEditing(prev => {
                const next = { ...prev };
                delete next[`remark_${id}`];
                return next;
            });
            fetchConfig();
        } catch (err) { toast.error(err.message); }
    };

    const saveTasks = async (task) => {
        try {
            const res = await fetch(`${API_BASE}/tasks/`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    text: (task.text || '').trim().toUpperCase(),
                    order: tasks.length,
                })
            });
            if (!res.ok) throw new Error('Failed to save task');
            toast.success('Task saved.');
            fetchConfig();
            fetchDropdowns();
        } catch (err) { toast.error(err.message); }
    };

    const deleteTasks = async (id) => {
        if (!window.confirm('Delete this task?')) return;
        try {
            const res = await fetch(`${API_BASE}/tasks/${id}/`, {
                method: 'DELETE',
                headers: authHeaders(false)
            });
            if (!res.ok) throw new Error('Failed to delete');
            toast.success('Task deleted.');
            fetchConfig();
            fetchDropdowns();
        } catch (err) { toast.error(err.message); }
    };

    const updateTasks = async (task, id) => {
        try {
            const res = await fetch(`${API_BASE}/tasks/${id}/`, {
                method: 'PATCH',
                headers: authHeaders(),
                body: JSON.stringify({
                    text: (task.text || '').trim().toUpperCase(),
                })
            });
            if (!res.ok) throw new Error('Failed to update task');
            toast.success('Task updated.');
            setEditing(prev => {
                const next = { ...prev };
                delete next[`task_${id}`];
                return next;
            });
            fetchConfig();
            fetchDropdowns();
        } catch (err) { toast.error(err.message); }
    };

    const renderTabs = () => (
        <div className="custom-tabs-container" style={{ marginBottom: 16 }}>
            <button
                className={`custom-tab ${activeTab === 'daily' ? 'active' : ''}`}
                onClick={() => setActiveTab('daily')}
            >
                Daily Report ({dailyCount})
            </button>
            <button
                className={`custom-tab ${activeTab === 'done' ? 'active' : ''}`}
                onClick={() => setActiveTab('done')}
            >
                Done ({doneCount})
            </button>
            <button
                className={`custom-tab ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
            >
                All Tasks ({allCount})
            </button>
            <button
                className={`custom-tab ${activeTab === 'generated' ? 'active' : ''}`}
                onClick={() => setActiveTab('generated')}
            >
                Daily Reports ({generatedCount})
            </button>
        </div>
    );

    const renderConfigModal = () => {
        if (!showConfig) return null;

        const handleDragEnd = (event) => {
            const { active, over } = event;
            if (over && active.id !== over.id) {
                setShippingLines((items) => {
                    const oldIndex = items.findIndex((item) => item.id === active.id);
                    const newIndex = items.findIndex((item) => item.id === over.id);
                    return arrayMove(items, oldIndex, newIndex);
                });
            }
        };

        const handleRemarkDragEnd = (event) => {
            const { active, over } = event;
            if (over && active.id !== over.id) {
                setRemarks((items) => {
                    const oldIndex = items.findIndex((item) => item.id === active.id);
                    const newIndex = items.findIndex((item) => item.id === over.id);
                    return arrayMove(items, oldIndex, newIndex);
                });
            }
        };

        const handleTaskDragEnd = (event) => {
            const { active, over } = event;
            if (over && active.id !== over.id) {
                setTasks((items) => {
                    const oldIndex = items.findIndex((item) => item.id === active.id);
                    const newIndex = items.findIndex((item) => item.id === over.id);
                    return arrayMove(items, oldIndex, newIndex);
                });
            }
        };

        const saveItemsOrder = async (items, path, label) => {
            setSavingOrder(true);
            try {
                await Promise.all(items.map((item, idx) =>
                    fetch(`${API_BASE}/${path}/${item.id}/`, {
                        method: 'PATCH',
                        headers: authHeaders(),
                        body: JSON.stringify({ order: idx }),
                    })
                ));
                toast.success(`${label} order saved.`);
                fetchConfig();
                fetchDropdowns();
            } catch (err) {
                toast.error('Failed to save order.');
            } finally {
                setSavingOrder(false);
            }
        };

        const handleSaveOrder = async () => {
            setSavingOrder(true);
            try {
                await Promise.all(shippingLines.map((line, idx) =>
                    fetch(`${API_BASE}/line-ordering/`, {
                        method: 'POST',
                        headers: authHeaders(),
                        body: JSON.stringify({
                            shipping_line_id: line.shipping_line_id || line.id,
                            order: idx,
                            color: line.color || '#FFFFFF',
                        })
                    })
                ));
                toast.success('Shipping line order saved.');
                fetchConfig();
                fetchData();
            } catch (err) {
                toast.error('Failed to save order.');
            } finally {
                setSavingOrder(false);
            }
        };

        return (
            <div className="modal-overlay" onClick={() => setShowConfig(false)}>
                <div className="dialog-container" style={{ width: '80vw', maxWidth: 900, height: '80vh', maxHeight: 800, display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                    <div className="dialog-header">
                        <h3>Shipping Tasks Configuration</h3>
                        <button type="button" className="close" onClick={() => setShowConfig(false)}>&times;</button>
                    </div>
                    <div className="dialog-body" style={{ padding: 16, flex: 1, overflow: 'auto' }}>
                        <div className="custom-tabs-container" style={{ marginBottom: 12 }}>
                            <button className={`custom-tab ${configTab === 'remarks' ? 'active' : ''}`} onClick={() => setConfigTab('remarks')}>Remarks</button>
                            <button className={`custom-tab ${configTab === 'tasks' ? 'active' : ''}`} onClick={() => setConfigTab('tasks')}>Tasks</button>
                            <button className={`custom-tab ${configTab === 'lines' ? 'active' : ''}`} onClick={() => setConfigTab('lines')}>Shipping Line Ordering</button>
                        </div>

                        {configTab === 'remarks' && (
                            <div>
                                {canCreate && (
                                    <div style={{ marginBottom: 20, background: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
                                        <h4>Add New Remark</h4>
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                className="search-input"
                                                placeholder="Remark text"
                                                style={{ flex: 2, backgroundColor: editing['__new_remark__']?.color || '#FFFFFF', color: getTextColorForBackground(editing['__new_remark__']?.color || '#FFFFFF') }}
                                                value={editing['__new_remark__']?.text || ''}
                                                onChange={e => setEditing(prev => ({ ...prev, __new_remark__: { ...(prev['__new_remark__'] || {}), text: e.target.value } }))}
                                            />
                                            <input
                                                type="color"
                                                value={editing['__new_remark__']?.color || '#FFFFFF'}
                                                onChange={e => setEditing(prev => ({ ...prev, __new_remark__: { ...(prev['__new_remark__'] || {}), color: e.target.value } }))}
                                                title="Select Remark Color"
                                                style={{ width: 60, height: 38, padding: '2px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
                                            />
                                            <button className="add-btn" onClick={() => { saveRemark(editing['__new_remark__'] || {}); setEditing(prev => ({ ...prev, __new_remark__: {} })); }}>Add</button>
                                        </div>
                                    </div>
                                )}

                                <h4>Existing Remarks</h4>
                                <p>Drag and drop to reorder remarks. Click "Save Order" to apply changes.</p>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleRemarkDragEnd}
                                >
                                    <SortableContext
                                        items={remarks.map(r => r.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '10px' }}>
                                            {remarks.map(r => (
                                                <SortableConfigRow key={r.id} id={r.id}>
                                                    {editing[`remark_${r.id}`] ? (
                                                        <>
                                                            <input
                                                                type="text"
                                                                className="search-input"
                                                                style={{ flex: 2, backgroundColor: editing[`remark_${r.id}`].color || '#FFFFFF', color: getTextColorForBackground(editing[`remark_${r.id}`].color || '#FFFFFF') }}
                                                                value={editing[`remark_${r.id}`].text}
                                                                onChange={e => setEditing(prev => ({ ...prev, [`remark_${r.id}`]: { ...(prev[`remark_${r.id}`] || r), text: e.target.value } }))}
                                                            />
                                                            <input
                                                                type="color"
                                                                value={editing[`remark_${r.id}`].color}
                                                                onChange={e => setEditing(prev => ({ ...prev, [`remark_${r.id}`]: { ...(prev[`remark_${r.id}`] || r), color: e.target.value } }))}
                                                                style={{ width: 60, height: 38 }}
                                                            />
                                                            {canEdit && <button className="icon-btn save-btn" onClick={() => updateRemark(editing[`remark_${r.id}`], r.id)}><i className="fa fa-save" /></button>}
                                                            <button className="icon-btn cancel-btn" onClick={() => setEditing(prev => { const n = { ...prev }; delete n[`remark_${r.id}`]; return n; })}><i className="fa fa-times" /></button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <strong style={{ flex: 2, backgroundColor: r.color || '#FFFFFF', color: getTextColorForBackground(r.color || '#FFFFFF'), padding: '4px 8px', borderRadius: '4px' }}>{r.text}</strong>
                                                            {canEdit && <button className="icon-btn" onClick={() => setEditing(prev => ({ ...prev, [`remark_${r.id}`]: { text: r.text, color: r.color || '#FFFFFF' } }))}><i className="fa fa-pencil" /></button>}
                                                            {canDelete && <button className="icon-btn cancel-btn" onClick={() => deleteRemark(r.id)}><i className="fa fa-trash" /></button>}
                                                        </>
                                                    )}
                                                </SortableConfigRow>
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                                {canEdit && (
                                    <button onClick={() => saveItemsOrder(remarks, 'remarks', 'Remark')} disabled={savingOrder} className="add-btn" style={{ marginTop: 20, width: '100%', padding: 12 }}>
                                        {savingOrder ? 'Saving...' : 'Save Order'}
                                    </button>
                                )}
                            </div>
                        )}

                        {configTab === 'tasks' && (
                            <div>
                                {canCreate && (
                                    <div style={{ marginBottom: 20, background: '#f9f9f9', padding: '15px', borderRadius: '8px' }}>
                                        <h4>Add New Task</h4>
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                className="search-input"
                                                placeholder="Task text"
                                                style={{ flex: 2 }}
                                                value={editing['__new_task__']?.text || ''}
                                                onChange={e => setEditing(prev => ({ ...prev, __new_task__: { ...(prev['__new_task__'] || {}), text: e.target.value } }))}
                                            />
                                            <button className="add-btn" onClick={() => { saveTasks(editing['__new_task__'] || {}); setEditing(prev => ({ ...prev, __new_task__: {} })); }}>Add</button>
                                        </div>
                                    </div>
                                )}

                                <h4>Existing Tasks</h4>
                                <p>Drag and drop to reorder tasks. Click "Save Order" to apply changes.</p>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleTaskDragEnd}
                                >
                                    <SortableContext
                                        items={tasks.map(t => t.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '10px' }}>
                                            {tasks.map(t => (
                                                <SortableConfigRow key={t.id} id={t.id}>
                                                    {editing[`task_${t.id}`] ? (
                                                        <>
                                                            <input
                                                                type="text"
                                                                className="search-input"
                                                                style={{ flex: 2 }}
                                                                value={editing[`task_${t.id}`].text}
                                                                onChange={e => setEditing(prev => ({ ...prev, [`task_${t.id}`]: { ...(prev[`task_${t.id}`] || t), text: e.target.value } }))}
                                                            />
                                                            {canEdit && <button className="icon-btn save-btn" onClick={() => updateTasks(editing[`task_${t.id}`], t.id)}><i className="fa fa-save" /></button>}
                                                            <button className="icon-btn cancel-btn" onClick={() => setEditing(prev => { const n = { ...prev }; delete n[`task_${t.id}`]; return n; })}><i className="fa fa-times" /></button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <strong style={{ flex: 2 }}>{t.text}</strong>
                                                            {canEdit && <button className="icon-btn" onClick={() => setEditing(prev => ({ ...prev, [`task_${t.id}`]: { text: t.text } }))}><i className="fa fa-pencil" /></button>}
                                                            {canDelete && <button className="icon-btn cancel-btn" onClick={() => deleteTasks(t.id)}><i className="fa fa-trash" /></button>}
                                                        </>
                                                    )}
                                                </SortableConfigRow>
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                                {canEdit && (
                                    <button onClick={() => saveItemsOrder(tasks, 'tasks', 'Task')} disabled={savingOrder} className="add-btn" style={{ marginTop: 20, width: '100%', padding: 12 }}>
                                        {savingOrder ? 'Saving...' : 'Save Order'}
                                    </button>
                                )}
                            </div>
                        )}

                        {configTab === 'lines' && (
                            <div>
                                <p>Drag and drop to reorder shipping lines. Click "Save Order" to apply changes.</p>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={shippingLines.map(l => l.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '10px' }}>
                                            {shippingLines.map(line => (
                                                <SortableShippingLineItem
                                                    key={line.id}
                                                    id={line.id}
                                                    line={line}
                                                    onColorChange={color => setShippingLines(prev => prev.map(l => l.id === line.id ? { ...l, color } : l))}
                                                />
                                            ))}
                                        </div>
                                    </SortableContext>
                                </DndContext>
                                {canEdit && (
                                    <button onClick={handleSaveOrder} disabled={savingOrder} className="add-btn" style={{ marginTop: 20, width: '100%', padding: 12 }}>
                                        {savingOrder ? 'Saving...' : 'Save Order'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderTaskModal = () => {
        if (!taskModal) return null;
        const { job, staff_id, task, done, remarks } = taskModal;
        const shippingStaff = (dropdowns.staff || []).filter(s =>
            (s.department_name || '').toUpperCase().includes('SHIPPING')
        );
        const staffOptions = [{ id: '', text: '- Select -' }, ...shippingStaff.map(s => ({ id: s.id, text: s.short_name || s.name }))];
        if (typeof staff_id === 'string' && staff_id.startsWith('new-staff:')) {
            staffOptions.push({ id: staff_id, text: staff_id.slice('new-staff:'.length) });
        }
        const remarkOptions = [{ id: '', text: '- Select -' }, ...dropdowns.remarks.map(r => ({ id: r.text, text: r.text, color: r.color }))];

        const createStaffOption = async (term) => {
            const text = (term || '').trim().toUpperCase();
            if (!text) return null;
            return { id: `new-staff:${text}`, text };
        };

        const onSave = () => {
            if (!task.trim()) { toast.warn('Task is required.'); return; }
            saveTask(job, { staff_id, task, done, remarks, task_id: null });
            setTaskModal(null);
        };

        return (
            <div className="modal-overlay" onClick={() => setTaskModal(null)}>
                <div className="dialog-container" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
                    <div className="dialog-header">
                        <h3>Add Task - Job {job.id}</h3>
                        <button type="button" className="close" onClick={() => setTaskModal(null)}>&times;</button>
                    </div>
                    <div className="dialog-body" style={{ padding: 16 }}>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Staff</label>
                            <Select2Dropdown
                                options={staffOptions}
                                value={staff_id}
                                onChange={val => setTaskModal(prev => ({ ...prev, staff_id: val }))}
                                placeholder="- Select Staff -"
                                valueKey="id"
                                displayKey="text"
                                allowCreate
                                onCreateTag={createStaffOption}
                            />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Task</label>
                            <Select2Dropdown
                                options={[{ id: '', text: '- Select -' }, ...(dropdowns.tasks || []).map(t => ({ id: t.text, text: t.text }))]}
                                value={task}
                                onChange={val => setTaskModal(prev => ({ ...prev, task: val }))}
                                placeholder="- Select Task -"
                                valueKey="id"
                                displayKey="text"
                            />
                        </div>
                        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input type="checkbox" id="tm-done" checked={done} onChange={e => setTaskModal(prev => ({ ...prev, done: e.target.checked }))} />
                            <label htmlFor="tm-done">Done</label>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Remarks</label>
                            <Select2Dropdown
                                options={remarkOptions}
                                value={remarks}
                                onChange={val => setTaskModal(prev => ({ ...prev, remarks: val }))}
                                placeholder="- Select Remarks -"
                                valueKey="id"
                                displayKey="text"
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button className="add-btn" onClick={onSave} style={{ width: 90, height: 36 }}>Save</button>
                            <button className="icon-btn cancel-btn" onClick={() => setTaskModal(null)} style={{ width: 90, height: 36 }}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderTaskCell = (task, jobId, field, readOnly = false) => {
        const shippingStaff = (dropdowns.staff || []).filter(s =>
            (s.department_name || '').toUpperCase().includes('SHIPPING')
        );
        const staffOptions = [{ id: '', text: '- Select -' }, ...shippingStaff.map(s => ({ id: s.id, text: s.short_name || s.name }))];
        const selectedStaff = getTaskEdit(task, jobId, 'staff_id');
        if (typeof selectedStaff === 'string' && selectedStaff.startsWith('new-staff:')) {
            staffOptions.push({ id: selectedStaff, text: selectedStaff.slice('new-staff:'.length) });
        }
        const remarkOptions = [{ id: '', text: '- Select -' }, ...dropdowns.remarks.map(r => ({ id: r.text, text: r.text, color: r.color }))];
        const taskOptions = [{ id: '', text: '- Select -' }, ...dropdowns.tasks.map(t => ({ id: t.text, text: t.text }))];
        const priorityOptions = [{ id: 'LOW', text: 'LOW' }, { id: 'MEDIUM', text: 'MEDIUM' }, { id: 'HIGH', text: 'HIGH' }];

        // Ad-hoc staff: keep the typed name as a pending option; it is stored
        // as free-text staff_name on the task when saved.
        const createStaffOption = async (term) => {
            const text = (term || '').trim().toUpperCase();
            if (!text) return null;
            return { id: `new-staff:${text}`, text };
        };

        const createTaskOption = async (term) => {
            const text = (term || '').trim().toUpperCase();
            if (!text) return null;
            try {
                const res = await fetch(`${API_BASE}/tasks/`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({ text, order: 0 })
                });
                if (!res.ok) throw new Error('Failed to add task');
                await fetchDropdowns();
                await fetchConfig();
                return { id: text, text };
            } catch (err) {
                toast.error(err.message);
                return null;
            }
        };

        const createRemarkOption = async (term) => {
            const text = (term || '').trim().toUpperCase();
            if (!text) return null;
            try {
                const res = await fetch(`${API_BASE}/remarks/`, {
                    method: 'POST',
                    headers: authHeaders(),
                    body: JSON.stringify({ text, color: '#FFFFFF', order: 0 })
                });
                if (!res.ok) throw new Error('Failed to add remark');
                await fetchDropdowns();
                await fetchConfig();
                return { id: text, text, color: '#FFFFFF' };
            } catch (err) {
                toast.error(err.message);
                return null;
            }
        };

        if (readOnly) {
            if (field === 'staff') return <span>{task.staff_short_name || task.staff_name || '-'}</span>;
            if (field === 'task') return <span>{task.task || '-'}</span>;
            if (field === 'priority') return <span>{task.priority || 'MEDIUM'}</span>;
            if (field === 'done') return <i className={`fa ${task.done ? 'fa-check' : 'fa-times'}`} style={{ color: task.done ? '#28a745' : '#dc3545' }} />;
            if (field === 'remarks') return <span style={{ backgroundColor: (dropdowns.remarks.find(r => r.text === (task.remarks || ''))?.color) || '#FFFFFF', padding: '2px 6px', borderRadius: 4 }}>{task.remarks || '-'}</span>;
            return null;
        }

        if (field === 'staff') return (
            <Select2Dropdown
                options={staffOptions}
                value={getTaskEdit(task, jobId, 'staff_id')}
                onChange={val => handleTaskEdit(task, jobId, 'staff_id', val)}
                valueKey="id"
                displayKey="text"
                style={{ minWidth: 120 }}
                allowCreate
                onCreateTag={createStaffOption}
            />
        );
        if (field === 'task') return (
            <Select2Dropdown
                options={taskOptions}
                value={getTaskEdit(task, jobId, 'task')}
                onChange={val => handleTaskEdit(task, jobId, 'task', val)}
                valueKey="id"
                displayKey="text"
                style={{ minWidth: 120 }}
                allowCreate
                onCreateTag={createTaskOption}
            />
        );
        if (field === 'done') return <input type="checkbox" checked={!!getTaskEdit(task, jobId, 'done')} onChange={e => handleTaskEdit(task, jobId, 'done', e.target.checked)} />;
        if (field === 'remarks') return (
            <Select2Dropdown
                options={remarkOptions}
                value={getTaskEdit(task, jobId, 'remarks')}
                onChange={val => handleTaskEdit(task, jobId, 'remarks', val)}
                valueKey="id"
                displayKey="text"
                style={{ minWidth: 140 }}
                clearable
                allowCreate
                onCreateTag={createRemarkOption}
            />
        );
        if (field === 'priority') return (
            <Select2Dropdown
                options={priorityOptions}
                value={getTaskEdit(task, jobId, 'priority')}
                onChange={val => handleTaskEdit(task, jobId, 'priority', val)}
                valueKey="id"
                displayKey="text"
                style={{ minWidth: 100 }}
            />
        );
        return null;
    };

    const renderDoneTable = () => (
        <table className="jobs-table" style={{ minWidth: 1500 }}>
            <thead>
                <tr>
                    <th style={stickyTh(LIST_STICKY[0])}>ID</th>
                    <th style={stickyTh(LIST_STICKY[1], { whiteSpace: 'pre-line' })}>{'IDF\nDATE IN'}</th>
                    <th style={stickyTh(LIST_STICKY[2])}>BL</th>
                    <th style={stickyTh(LIST_STICKY[3])}>Shipping Line</th>
                    <th>Customer</th>
                    <th>Vessel</th>
                    <th style={{ whiteSpace: 'pre-line' }}>{'Manifest\nOut'}</th>
                    <th style={{ whiteSpace: 'pre-line' }}>{'ETA\nETB\nATB'}</th>
                    <th>Cont. Size</th>
                    <th>Nomination</th>
                    <th>SL User</th>
                    <th>Action</th>
                </tr>
            </thead>
            <tbody>
                {doneJobRows.map(({ jobRow, tasks: doneTasks }, i) => {
                    const key = `donejob-${jobRow.id}`;
                    const lineKey = String(jobRow.shipping_line_id ?? 'none');
                    const prevLineKey = i > 0 ? String(doneJobRows[i - 1].jobRow.shipping_line_id ?? 'none') : lineKey;
                    const showSeparator = i > 0 && lineKey !== prevLineKey;
                    return (
                        <React.Fragment key={key}>
                            {showSeparator && (
                                <tr className="shipping-line-separator">
                                    <td colSpan={12} style={{ height: 14, backgroundColor: '#e9ecef', border: 'none', padding: 0 }} />
                                </tr>
                            )}
                            <tr>
                                <td style={stickyTd(LIST_STICKY[0])}>
                                    <CopyCell text={jobRow.id}>
                                        <button
                                            type="button"
                                            onClick={() => openJobFormModal(jobRow)}
                                            style={{ border: 'none', background: 'transparent', color: '#0056b3', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                                        >
                                            {jobRow.id}
                                        </button>
                                    </CopyCell>
                                </td>
                                <td style={stickyTd(LIST_STICKY[1], '#fff', { whiteSpace: 'normal', wordBreak: 'break-word' })}>{renderIdfCell(jobRow)}</td>
                                <td style={stickyTd(LIST_STICKY[2])}><CopyCell text={jobRow.bl}>{renderBlLink(jobRow)}</CopyCell></td>
                                <td style={stickyTd(LIST_STICKY[3])}>
                                    <CopyCell text={`${jobRow.shipping_line || ''} ${jobRow.bl_type || ''} ${jobRow.do_status || ''}`.trim()}>
                                        <div>{renderShippingLine(jobRow)}</div>
                                        <div style={{ color: '#dc3545' }}>{toUpper(jobRow.bl_type)}</div>
                                        {jobRow.do_status && (
                                            <div style={{ fontSize: 11 }}>{toUpper(jobRow.do_status)}</div>
                                        )}
                                    </CopyCell>
                                </td>
                                <td>
                                    <CopyCell text={`${jobRow.customer_name || ''} ${jobRow.client_ref || ''}`.trim()}>
                                        <div><strong>{renderCustomer(jobRow)}</strong></div>
                                        <div>{toUpper(jobRow.client_ref)}</div>
                                    </CopyCell>
                                </td>
                                <td><CopyCell text={jobRow.vessel}>{toUpper(jobRow.vessel)}</CopyCell></td>
                                <td style={{ textAlign: 'center' }}>{renderManifestOut(jobRow)}</td>
                                <td><CopyCell text={`ETA: ${jobRow.eta || '-'} ETB: ${jobRow.etb || '-'} ATB: ${jobRow.atb || '-'}`}>{renderEtaEtbAtb(jobRow)}</CopyCell></td>
                                <td><CopyCell text={jobRow.container_size}>{renderContainerSize(jobRow)}</CopyCell></td>
                                <td style={{ textAlign: 'center' }}>{toUpper(jobRow.cargo_type) === 'LCL' ? '' : renderIcdNomination(jobRow)}</td>
                                <td><CopyCell text={jobRow.sl_user}>{jobRow.sl_user_initials || '-'}</CopyCell></td>
                                <td style={{ textAlign: 'center' }}>
                                    <button
                                        className="icon-btn"
                                        onClick={() => setDoneTasksModal({ job: jobRow, tasks: doneTasks })}
                                        title="View done tasks"
                                        style={{ color: '#0056b3' }}
                                    >
                                        <i className="fa fa-eye" />
                                    </button>
                                </td>
                            </tr>
                        </React.Fragment>
                    );
                })}
                {!doneJobRows.length && (
                    <tr><td colSpan={12} className="no-data">No records found.</td></tr>
                )}
            </tbody>
        </table>
    );

    const renderAllTable = () => (
        <table className="jobs-table" style={{ minWidth: 1600 }}>
            <thead>
                <tr>
                    <th style={stickyTh(LIST_STICKY[0])}>ID</th>
                    <th style={stickyTh(LIST_STICKY[1], { whiteSpace: 'pre-line' })}>{'IDF\nDATE IN'}</th>
                    <th style={stickyTh(LIST_STICKY[2])}>BL</th>
                    <th style={stickyTh(LIST_STICKY[3])}>Shipping Line</th>
                    <th>Customer</th>
                    <th>Vessel</th>
                    <th style={{ whiteSpace: 'pre-line' }}>{'Manifest\nOut'}</th>
                    <th>Cont. Size</th>
                    <th>Nomination</th>
                    <th>SL User</th>
                    <th>Staff</th>
                    <th>Task</th>
                    <th>Remarks</th>
                    <th>Report Date</th>
                    <th>Done</th>
                    <th>Done Date</th>
                </tr>
            </thead>
            <tbody>
                {filteredRows.map((row, i) => {
                    const lineKey = String(row.shipping_line_id ?? 'none');
                    const prevLineKey = i > 0 ? String(filteredRows[i - 1].shipping_line_id ?? 'none') : lineKey;
                    const showSeparator = i > 0 && lineKey !== prevLineKey;
                    return (
                        <React.Fragment key={getDoneRowKey(row)}>
                            {showSeparator && (
                                <tr className="shipping-line-separator">
                                    <td colSpan={16} style={{ height: 14, backgroundColor: '#e9ecef', border: 'none', padding: 0 }} />
                                </tr>
                            )}
                            <tr>
                                <td style={stickyTd(LIST_STICKY[0])}>
                                    <CopyCell text={row.id}>
                                        <button
                                            type="button"
                                            onClick={() => openJobFormModal(row)}
                                            style={{ border: 'none', background: 'transparent', color: '#0056b3', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                                        >
                                            {row.id}
                                        </button>
                                    </CopyCell>
                                </td>
                                <td style={stickyTd(LIST_STICKY[1], '#fff', { whiteSpace: 'normal', wordBreak: 'break-word' })}>{renderIdfCell(row)}</td>
                                <td style={stickyTd(LIST_STICKY[2])}><CopyCell text={row.bl}>{renderBlLink(row)}</CopyCell></td>
                                <td style={stickyTd(LIST_STICKY[3])}>
                                    <CopyCell text={`${row.shipping_line || ''} ${row.bl_type || ''} ${row.do_status || ''}`.trim()}>
                                        <div>{renderShippingLine(row)}</div>
                                        <div style={{ color: '#dc3545' }}>{toUpper(row.bl_type)}</div>
                                        {row.do_status && (
                                            <div style={{ fontSize: 11 }}>{toUpper(row.do_status)}</div>
                                        )}
                                    </CopyCell>
                                </td>
                                <td>
                                    <CopyCell text={`${row.customer_name || ''} ${row.client_ref || ''}`.trim()}>
                                        <div><strong>{renderCustomer(row)}</strong></div>
                                        <div>{toUpper(row.client_ref)}</div>
                                    </CopyCell>
                                </td>
                                <td><CopyCell text={row.vessel}>{toUpper(row.vessel)}</CopyCell></td>
                                <td style={{ textAlign: 'center' }}>{renderManifestOut(row)}</td>
                                <td><CopyCell text={row.container_size}>{renderContainerSize(row)}</CopyCell></td>
                                <td style={{ textAlign: 'center' }}>{toUpper(row.cargo_type) === 'LCL' ? '' : renderIcdNomination(row)}</td>
                                <td><CopyCell text={row.sl_user}>{row.sl_user_initials || '-'}</CopyCell></td>
                                <td><CopyCell text={row.staff_short_name || row.staff_name}>{toUpper(row.staff_short_name || row.staff_name) || '-'}</CopyCell></td>
                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 180 }}><CopyCell text={row.task}>{toUpper(row.task) || '-'}</CopyCell></td>
                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 180 }}><CopyCell text={row.remarks}>{toUpper(row.remarks) || '-'}</CopyCell></td>
                                <td>{fmtDateShort(row.report_date) || '-'}</td>
                                <td style={{ textAlign: 'center' }}>{renderDone(row.done)}</td>
                                <td>{fmtDateShort(row.done_at) || '-'}</td>
                            </tr>
                        </React.Fragment>
                    );
                })}
                {!filteredRows.length && (
                    <tr><td colSpan={16} className="no-data">No records found.</td></tr>
                )}
            </tbody>
        </table>
    );

    const openJobFormModal = async (job) => {
        try {
            const res = await fetch(`/api/documentation/jobs/${job.id}/`, { headers: authHeaders(false) });
            if (!res.ok) {
                toast.error('Failed to load job details');
                return;
            }
            const data = await res.json();
            setJobFormModal({ isOpen: true, job: data });
        } catch (e) {
            console.error(e);
            toast.error('Error loading job');
        }
    };

    const closeJobFormModal = (refresh = false) => {
        setJobFormModal({ isOpen: false, job: null });
        if (refresh) {
            fetchData();
        }
    };

    const renderBlLink = (row) => {
        const isLcl = (row.shipment_type || row.cargo_type || '').toString().toUpperCase() === 'LCL';
        const title = isLcl ? `MBL: ${row.mbl_no || row.mblNo || '-'}` : undefined;
        const blText = toUpper(row.bl);
        if (!canEditShippingReport) {
            return <span title={title}>{blText}</span>;
        }
        return (
            <button
                type="button"
                title={title}
                onClick={() => setShippingDetailsRow(row)}
                style={{ border: 'none', background: 'transparent', color: '#0056b3', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', padding: 0 }}
            >
                {blText}
            </button>
        );
    };

    return (
        <div style={{ padding: '20px 20px 0', overflowX: 'auto' }}>
            <div className="toolbar" style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                {activeTab === 'done' || activeTab === 'all' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label style={{ fontWeight: 600 }}>From</label>
                        <input type="date" className="search-input" value={activeTab === 'done' ? doneFrom : allFrom} onChange={e => (activeTab === 'done' ? setDoneFrom : setAllFrom)(e.target.value)} />
                        <label style={{ fontWeight: 600 }}>To</label>
                        <input type="date" className="search-input" value={activeTab === 'done' ? doneTo : allTo} onChange={e => (activeTab === 'done' ? setDoneTo : setAllTo)(e.target.value)} />
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label style={{ fontWeight: 600 }}>Report Date</label>
                        <input type="date" className="search-input" value={reportDate} onChange={e => setReportDate(e.target.value)} />
                    </div>
                )}

                <input className="search-input" placeholder="Search IDF / BL / Customer / Vessel..." value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 250 }} />

                {activeTab !== 'generated' && (
                    <div style={{ width: 200 }}>
                        <Select2Dropdown
                            options={[
                                { id: '', text: 'All SL Users' },
                                { id: '__NONE__', text: 'Not Assigned' },
                                ...shippingStaff.map(s => ({ id: s.name, text: s.name }))
                            ]}
                            value={slUserFilter}
                            onChange={setSlUserFilter}
                            valueKey="id"
                            displayKey="text"
                            placeholder="Filter by SL User"
                        />
                    </div>
                )}

                {activeTab !== 'generated' && (
                    <div style={{ width: 160 }}>
                        <Select2Dropdown
                            options={[
                                { id: '', text: 'Manifest Out: All' },
                                { id: 'out', text: 'Manifest Out' },
                                { id: 'pending', text: 'Manifest Pending' },
                            ]}
                            value={manifestFilter}
                            onChange={setManifestFilter}
                            valueKey="id"
                            displayKey="text"
                            placeholder="Filter by Manifest"
                        />
                    </div>
                )}

                {activeTab === 'all' && (
                    <>
                        <div style={{ width: 180 }}>
                            <Select2Dropdown
                                options={allStaffOptions}
                                value={allStaffFilter}
                                onChange={setAllStaffFilter}
                                valueKey="id"
                                displayKey="text"
                                placeholder="Filter by Staff"
                            />
                        </div>
                        <div style={{ width: 140 }}>
                            <Select2Dropdown
                                options={[
                                    { id: '', text: 'All Status' },
                                    { id: 'pending', text: 'Pending' },
                                    { id: 'done', text: 'Done' },
                                ]}
                                value={allStatusFilter}
                                onChange={setAllStatusFilter}
                                valueKey="id"
                                displayKey="text"
                                placeholder="Pending / Done"
                            />
                        </div>
                    </>
                )}

                {activeTab === 'daily' && (
                    <button className="add-btn" style={{ background: '#0056b3' }} onClick={openPreview}>
                        Preview Daily Report
                    </button>
                )}
                {activeTab === 'daily' && (canCreate || canEdit) && (
                    <button className="add-btn" style={{ background: '#3f6b32' }} onClick={() => generateReport()} disabled={generating}>
                        {generating ? 'Generating...' : 'Generate Daily Report'}
                    </button>
                )}

                <button className="icon-btn" title="Configuration" onClick={() => setShowConfig(true)} style={{ marginLeft: 'auto', fontSize: 18 }}>
                    <i className="fa-solid fa-cogs" />
                </button>
            </div>

            {activeTab === 'daily' && dailyCards && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 12 }}>
                    <button
                        onClick={() => setCardFilter('')}
                        style={{
                            background: cardFilter === '' ? '#374151' : '#f3f4f6',
                            border: '2px solid #9ca3af',
                            borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                            minWidth: 70, transition: 'background 0.15s',
                            outline: cardFilter === '' ? '2px solid #374151' : 'none', outlineOffset: 2,
                        }}
                    >
                        <span style={{ fontSize: 10, color: cardFilter === '' ? '#e0e0e0' : '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>All Daily Jobs</span>
                        <span style={{ fontWeight: 'bold', fontSize: 14, color: cardFilter === '' ? '#fff' : '#374151', lineHeight: 1 }}>{dailyCards.all}</span>
                    </button>
                    <button
                        onClick={() => setCardFilter(cardFilter === 'TASKS' ? '' : 'TASKS')}
                        style={{
                            background: cardFilter === 'TASKS' ? '#166534' : '#dcfce7',
                            border: '2px solid #86efac',
                            borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                            minWidth: 70, transition: 'background 0.15s',
                            outline: cardFilter === 'TASKS' ? '2px solid #166534' : 'none', outlineOffset: 2,
                        }}
                    >
                        <span style={{ fontSize: 10, color: cardFilter === 'TASKS' ? '#e0e0e0' : '#166534', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Tasks Only</span>
                        <span style={{ fontWeight: 'bold', fontSize: 14, color: cardFilter === 'TASKS' ? '#fff' : '#166534', lineHeight: 1 }}>{dailyCards.tasksOnly}</span>
                    </button>
                    {dailyCards.staff.map((s, i) => {
                        const palette = s.name === 'UNASSIGNED'
                            ? { bg: '#f3f4f6', border: '#9ca3af', text: '#4b5563' }
                            : STAFF_CARD_COLORS[i % STAFF_CARD_COLORS.length];
                        const isActive = cardFilter === s.name;
                        return (
                            <button
                                key={s.name}
                                onClick={() => setCardFilter(isActive ? '' : s.name)}
                                style={{
                                    background: isActive ? palette.text : palette.bg,
                                    border: `2px solid ${palette.border}`,
                                    borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                                    minWidth: 70, transition: 'background 0.15s, color 0.15s',
                                    outline: isActive ? `2px solid ${palette.text}` : 'none', outlineOffset: 2,
                                }}
                            >
                                <span style={{ fontSize: 10, color: isActive ? '#e0e0e0' : palette.text, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{s.name}</span>
                                <span style={{ fontWeight: 'bold', fontSize: 14, color: isActive ? '#fff' : palette.text, lineHeight: 1 }}>{s.count}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {renderTabs()}

            {activeTab === 'generated' ? (
                <TableScrollContainer>
                    <table className="jobs-table" style={{ overflow: 'visible' }}>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Time</th>
                                <th>Generated By</th>
                                <th style={{ textAlign: 'center' }}>View</th>
                                <th style={{ textAlign: 'center' }}>PDF</th>
                                <th style={{ textAlign: 'center' }}>Excel</th>
                                {canDelete && <th style={{ textAlign: 'center' }}>Delete</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {generatedByDate.map(([date, reports]) =>
                                reports.map((r, i) => (
                                    <tr key={r.id}>
                                        {i === 0 && (
                                            <td rowSpan={reports.length} style={{ verticalAlign: 'middle', fontWeight: 600, borderRight: '1px solid #dee2e6' }}>
                                                {fmtDateDmy(date) || date}
                                            </td>
                                        )}
                                        <td>{fmtTime(r.generated_at)}</td>
                                        <td>{r.generated_by || '-'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button className="icon-btn" title="View report" onClick={() => openReportView(r)}>
                                                <i className="fa fa-eye" />
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button className="icon-btn" title="Download PDF" onClick={() => downloadGenerated(r, 'pdf')}>
                                                <i className="fa fa-file-pdf" />
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button className="icon-btn" title="Download Excel" onClick={() => downloadGenerated(r, 'excel')}>
                                                <i className="fa fa-file-excel" />
                                            </button>
                                        </td>
                                        {canDelete && (
                                            <td style={{ textAlign: 'center' }}>
                                                <button
                                                    className="icon-btn"
                                                    title="Delete report"
                                                    onClick={() => setDeleteReportConfirm(r)}
                                                    style={{ color: '#dc3545' }}
                                                >
                                                    <i className="fa fa-trash" />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                            {!generated.length && <tr><td colSpan={canDelete ? 7 : 6} className="no-data">No generated reports.</td></tr>}
                        </tbody>
                    </table>
                </TableScrollContainer>
            ) : (
                <TableScrollContainer>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
                    ) : activeTab === 'done' ? renderDoneTable() : activeTab === 'all' ? renderAllTable() : (
                        <>
                        {(canCreate || canEdit) && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginBottom: 6, minWidth: 1800 }}>
                                {canCreate && (
                                    <button
                                        type="button"
                                        className="icon-btn"
                                        title="Create task for a DO Received job"
                                        onClick={() => setAddTaskDialog({ value: '', options: [], activeIdx: -1, job: null })}
                                        disabled={bulkSaving}
                                        style={{ fontSize: 16, color: '#0056b3', border: '1px solid #0056b3', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', background: '#fff' }}
                                    >
                                        <i className="fa fa-plus" />
                                    </button>
                                )}
                                {canCreate && (
                                    <button
                                        type="button"
                                        onClick={bulkMode === 'create' ? handleBulkSave : startBulkCreate}
                                        disabled={bulkSaving || (!!bulkMode && bulkMode !== 'create')}
                                        title={bulkMode === 'create' ? 'Save all new tasks' : 'Add a task row to every visible job'}
                                        style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#28a745', border: '1px solid #1e7e34', borderRadius: 4, padding: '5px 14px', cursor: 'pointer', opacity: bulkSaving || (!!bulkMode && bulkMode !== 'create') ? 0.6 : 1 }}
                                    >
                                        {bulkSaving && bulkMode === 'create' ? 'Saving…' : bulkMode === 'create' ? 'Bulk Save' : 'Bulk Create'}
                                    </button>
                                )}
                                {canEdit && (
                                    <button
                                        type="button"
                                        onClick={bulkMode === 'edit' ? handleBulkSave : startBulkEdit}
                                        disabled={bulkSaving || (!!bulkMode && bulkMode !== 'edit')}
                                        title={bulkMode === 'edit' ? 'Save all edited tasks' : 'Edit all visible tasks'}
                                        style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#fd7e14', border: '1px solid #d9690a', borderRadius: 4, padding: '5px 14px', cursor: 'pointer', opacity: bulkSaving || (!!bulkMode && bulkMode !== 'edit') ? 0.6 : 1 }}
                                    >
                                        {bulkSaving && bulkMode === 'edit' ? 'Saving…' : bulkMode === 'edit' ? 'Bulk Save' : 'Bulk Edit'}
                                    </button>
                                )}
                                {bulkMode && (
                                    <button
                                        type="button"
                                        className="icon-btn"
                                        title="Cancel bulk editing"
                                        onClick={cancelBulk}
                                        disabled={bulkSaving}
                                        style={{ fontSize: 14, color: '#dc3545', border: '1px solid #dc3545', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', background: '#fff' }}
                                    >
                                        <i className="fa fa-times" />
                                    </button>
                                )}
                            </div>
                        )}
                        <table className="jobs-table" style={{ minWidth: 1800, overflow: 'visible' }}>
                            <thead>
                                <tr>
                                    <th style={stickyTh(DAILY_STICKY[0])}>ID</th>
                                    <th style={stickyTh(DAILY_STICKY[1], { whiteSpace: 'pre-line' })}>{'IDF\nDATE IN'}</th>
                                    <th style={stickyTh(DAILY_STICKY[2])}>Customer</th>
                                    <th style={stickyTh(DAILY_STICKY[3])}>BL</th>
                                    <th>Shipping Line</th>
                                    <th>Port Remarks</th>
                                    <th style={{ whiteSpace: 'pre-line' }}>{'Manifest\nOut'}</th>
                                    <th style={{ whiteSpace: 'pre-line' }}>{'ETA\nETB\nATB'}</th>
                                    <th>Cont. Size</th>
                                    <th>Nomination</th>
                                    <th>SL User</th>
                                    <th>Staff</th>
                                    <th>Task</th>
                                    <th>Remarks</th>
                                    <th>Priority</th>
                                    <th>Done</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDailyDragEnd}>
                                    {dailyGroups.map((group, gi) => (
                                        <SortableContext key={group.key} items={group.rows.map(r => r._clientKey)} strategy={verticalListSortingStrategy}>
                                            {gi > 0 && (
                                                <tr className="shipping-line-separator">
                                                    <td colSpan={17} style={{ height: 14, backgroundColor: '#e9ecef', border: 'none', padding: 0 }} />
                                                </tr>
                                            )}
                                            {group.rows.map(row => {
                                                const editMode = editingRows[row._clientKey];
                                                const isEditing = !!editMode;
                                                return (
                                                <SortableRow
                                                    key={getDoneRowKey(row)}
                                                    id={row._clientKey}
                                                    disabled={!canEdit || isEditing}
                                                    className={flashTaskId && row.task_id === flashTaskId ? 'str-save-flash' : undefined}
                                                    style={row.pending_receipt ? { backgroundColor: '#fff3cd' } : {}}
                                                    title={row.pending_receipt ? 'PENDING RECEIPT' : undefined}
                                                >
                                                    {({ listeners, attributes }) => (<>
                                                        <td style={stickyTd(DAILY_STICKY[0], row.pending_receipt ? '#fff3cd' : '#fff')}>
                                                            <CopyCell text={row.id}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openJobFormModal(row)}
                                                                    style={{ border: 'none', background: 'transparent', color: '#0056b3', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                                                                >
                                                                    {row.id}
                                                                </button>
                                                            </CopyCell>
                                                        </td>
                                                        <td style={stickyTd(DAILY_STICKY[1], row.pending_receipt ? '#fff3cd' : '#fff', { whiteSpace: 'normal', wordBreak: 'break-word' })}>{renderIdfCell(row)}</td>
                                                        <td style={stickyTd(DAILY_STICKY[2], row.pending_receipt ? '#fff3cd' : '#fff', { whiteSpace: 'normal', wordBreak: 'break-word' })}>
                                                            <CopyCell text={`${row.customer_name || ''} ${row.client_ref || ''}`.trim()}>
                                                                <div><strong>{renderCustomer(row)}</strong></div>
                                                                <div>{toUpper(row.client_ref)}</div>
                                                                {row.duty_paid ? (
                                                                    row.shipping_acknowledge_duty ? (
                                                                        <div style={{ color: '#28a745', fontWeight: 700 }} title={`Duty paid — acknowledged${row.shipping_acknowledge_duty_by ? ` by ${row.shipping_acknowledge_duty_by}` : ''}`}>DUTY PAID</div>
                                                                    ) : (
                                                                        <div
                                                                            className="str-duty-paid-flash"
                                                                            title={canEdit ? 'Duty paid — double-click to acknowledge' : 'Duty paid'}
                                                                            onDoubleClick={canEdit ? () => acknowledgeDutyPaid(row) : undefined}
                                                                            style={{ cursor: canEdit ? 'pointer' : 'default' }}
                                                                        >
                                                                            DUTY PAID
                                                                        </div>
                                                                    )
                                                                ) : (
                                                                    <div>DUTY NOT PAID</div>
                                                                )}
                                                            </CopyCell>
                                                        </td>
                                                        <td style={stickyTd(DAILY_STICKY[3], row.pending_receipt ? '#fff3cd' : '#fff')}
                                                            className={row.duty_paid && String(row.do_status || '').toUpperCase().includes('DUTY') ? 'str-bl-flash' : undefined}>
                                                            <CopyCell text={`${row.bl || ''} ${row.vessel || ''}`.trim()}>
                                                                <div>{renderBlLink(row)}</div>
                                                                {row.vessel && <div>{toUpper(row.vessel)}</div>}
                                                            </CopyCell>
                                                        </td>
                                                        <td>
                                                            <CopyCell text={`${row.shipping_line || ''} ${row.bl_type || ''} ${row.do_status || ''}`.trim()}>
                                                                <div>{renderShippingLine(row)}</div>
                                                                <div style={{ color: '#dc3545' }}>{toUpper(row.bl_type)}</div>
                                                                {row.do_status && (
                                                                    <div style={{ fontSize: 11 }}>{toUpper(row.do_status)}</div>
                                                                )}
                                                            </CopyCell>
                                                        </td>
                                                        <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 120, maxWidth: 220, color: '#dc3545' }}><CopyCell text={row.port_remarks}>{toUpper(row.port_remarks) || '-'}</CopyCell></td>
                                                        <td style={{ textAlign: 'center' }}>{renderManifestOut(row)}</td>
                                                        <td><CopyCell text={`ETA: ${row.eta || '-'} ETB: ${row.etb || '-'} ATB: ${row.atb || '-'}`}>{renderEtaEtbAtb(row)}</CopyCell></td>
                                                        <td><CopyCell text={row.container_size}>{renderContainerSize(row)}</CopyCell></td>
                                                        <td style={{ textAlign: 'center' }}>{toUpper(row.cargo_type) === 'LCL' ? '' : renderIcdNomination(row)}</td>
                                                        <td><CopyCell text={row.sl_user}>{row.sl_user_initials || '-'}</CopyCell></td>
                                                        <td>{isEditing ? renderTaskCell(row, row.id, 'staff', false) : <CopyCell text={row.staff_short_name || row.staff_name}>{renderTaskCell(row, row.id, 'staff', true)}</CopyCell>}</td>
                                                        <td>{isEditing ? renderTaskCell(row, row.id, 'task', false) : <CopyCell text={row.task}>{renderTaskCell(row, row.id, 'task', true)}</CopyCell>}</td>
                                                        <td>{isEditing ? renderTaskCell(row, row.id, 'remarks', false) : <CopyCell text={row.remarks}>{renderTaskCell(row, row.id, 'remarks', true)}</CopyCell>}</td>
                                                        <td style={priorityCellStyle(getTaskEdit(row, row.id, 'priority'))}>
                                                            {isEditing ? renderTaskCell(row, row.id, 'priority', false) : <CopyCell text={row.priority}>{renderTaskCell(row, row.id, 'priority', true)}</CopyCell>}
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {isEditing ? (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!getTaskEdit(row, row.id, 'done')}
                                                                    disabled={editMode !== 'edit'}
                                                                    onChange={e => handleTaskEdit(row, row.id, 'done', e.target.checked)}
                                                                />
                                                            ) : renderDone(row.done)}
                                                        </td>
                                                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                            {isEditing ? (
                                                                <>
                                                                    <button
                                                                        className="icon-btn save-btn"
                                                                        onClick={() => saveTask({ id: row.id }, row)}
                                                                        disabled={saving[row._clientKey]}
                                                                        title="Save"
                                                                    >
                                                                        <i className="fa fa-save" />
                                                                    </button>
                                                                    <button
                                                                        className="icon-btn cancel-btn"
                                                                        onClick={() => cancelTaskEdit(row)}
                                                                        title="Cancel"
                                                                    >
                                                                        <i className="fa fa-times" />
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {canEdit && (
                                                                        <i
                                                                            className="fa fa-grip-vertical"
                                                                            {...attributes}
                                                                            {...listeners}
                                                                            title="Drag to reorder"
                                                                            style={{ cursor: 'grab', color: '#adb5bd', marginRight: 6, fontSize: 14 }}
                                                                        />
                                                                    )}
                                                                    {canCreate && (
                                                                        <button
                                                                            className="icon-btn"
                                                                            onClick={() => addTaskToJob(row)}
                                                                            title="Add task"
                                                                        >
                                                                            <i className="fa fa-plus" />
                                                                        </button>
                                                                    )}
                                                                    {canEdit && row.task_id && (
                                                                        <button
                                                                            className="icon-btn"
                                                                            onClick={() => startTaskEdit(row)}
                                                                            title="Edit task"
                                                                        >
                                                                            <i className="fa fa-pencil" />
                                                                        </button>
                                                                    )}
                                                                    {canDelete && row.task_id && (
                                                                        <button
                                                                            className="icon-btn"
                                                                            onClick={() => setDeleteTaskConfirm(row)}
                                                                            title="Delete task"
                                                                            style={{ color: '#dc3545' }}
                                                                        >
                                                                            <i className="fa fa-trash" />
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </td>
                                                    </>)}
                                                </SortableRow>
                                                );
                                            })}
                                        </SortableContext>
                                    ))}
                                </DndContext>
                                {!tableRows.length && (
                                    <tr><td colSpan={17} className="no-data">No records found.</td></tr>
                                )}
                            </tbody>
                        </table>
                        </>
                    )}
                </TableScrollContainer>
            )}

            {jobFormModal.isOpen && (
                <div className="modal-overlay" onClick={() => closeJobFormModal(false)}>
                    <div
                        className="dialog-container"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '95%',
                            maxWidth: 1400,
                            height: '95vh',
                            display: 'flex',
                            flexDirection: 'column',
                            margin: 'auto',
                        }}
                    >
                        <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Job Details (View Only)</h3>
                            <button className="icon-btn" onClick={() => closeJobFormModal(false)} title="Close">
                                <i className="fa fa-times" />
                            </button>
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1, padding: '0 20px 20px' }}>
                            <JobForm
                                mode="view"
                                initialData={jobFormModal.job}
                                onClose={closeJobFormModal}
                                documentsEditable={false}
                            />
                        </div>
                    </div>
                </div>
            )}

            <ShippingDetailsDialog
                open={!!shippingDetailsRow}
                row={shippingDetailsRow}
                onClose={() => setShippingDetailsRow(null)}
                onSaved={() => fetchData(true)}
                canEdit={canEditShippingReport}
                canDelete={canDeleteShippingReport}
            />

            {doneTasksModal && (
                <div className="modal-overlay" onClick={() => setDoneTasksModal(null)}>
                    <div className="dialog-container" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h3>Done Tasks{doneTasksModal.job.idf_no ? ` - ${toUpper(doneTasksModal.job.idf_no)}` : ''}</h3>
                            <button type="button" className="close" onClick={() => setDoneTasksModal(null)}>&times;</button>
                        </div>
                        <div style={{ padding: 16, overflow: 'auto', maxHeight: '70vh' }}>
                            <table className="jobs-table" style={{ minWidth: 0, width: '100%' }}>
                                <thead>
                                    <tr>
                                        <th>Task</th>
                                        <th>Staff</th>
                                        <th>Done Date</th>
                                        {canDelete && <th>Action</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {doneTasksModal.tasks.map(t => (
                                        <tr key={t._clientKey}>
                                            <td><CopyCell text={t.task}>{t.task || '-'}</CopyCell></td>
                                            <td><CopyCell text={t.staff_short_name || t.staff_name}>{t.staff_short_name || t.staff_name || '-'}</CopyCell></td>
                                            <td><CopyCell text={(t.done_at || '').slice(0, 10)}>{fmtDateDmy((t.done_at || '').slice(0, 10)) || '-'}</CopyCell></td>
                                            {canDelete && (
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        className="icon-btn"
                                                        title="Mark as NOT DONE"
                                                        onClick={() => setUndoneTaskConfirm({ job: doneTasksModal.job, task: t })}
                                                        style={{ color: '#c62828' }}
                                                    >
                                                        <i className="fa fa-undo" />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {addTaskDialog && (
                <div className="modal-overlay" onClick={() => setAddTaskDialog(null)}>
                    <div className="dialog-container" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h3>Create Task - DO Received Job</h3>
                            <button type="button" className="close" onClick={() => setAddTaskDialog(null)}>&times;</button>
                        </div>
                        <div className="dialog-body" style={{ padding: 16 }}>
                            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Search IDF</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    value={addTaskDialog.value}
                                    onChange={e => setAddTaskDialog(p => ({ ...p, value: e.target.value, job: null, activeIdx: -1 }))}
                                    onKeyDown={e => {
                                        const opts = addTaskDialog.options || [];
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            setAddTaskDialog(p => ({ ...p, activeIdx: Math.min((p.activeIdx ?? -1) + 1, opts.length - 1) }));
                                        } else if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            setAddTaskDialog(p => ({ ...p, activeIdx: Math.max((p.activeIdx ?? -1) - 1, 0) }));
                                        } else if (e.key === 'Enter') {
                                            if (addTaskDialog.activeIdx >= 0 && opts[addTaskDialog.activeIdx]) {
                                                e.preventDefault();
                                                const job = opts[addTaskDialog.activeIdx];
                                                setAddTaskDialog(p => ({ ...p, job, value: job.idf_no || String(job.id), options: [] }));
                                            }
                                        } else if (e.key === 'Escape') {
                                            setAddTaskDialog(p => ({ ...p, options: [], activeIdx: -1 }));
                                        }
                                    }}
                                    placeholder="Type IDF to search DO Received jobs..."
                                    autoFocus
                                    style={{ fontSize: 13, padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', width: '100%', height: 32, boxSizing: 'border-box', background: '#fff', color: '#222' }}
                                />
                                {(addTaskDialog.options || []).length > 0 && (
                                    <div style={{ position: 'absolute', top: 'calc(100% + 2px)', left: 0, width: '100%', background: '#fff', border: '1px solid #ccc', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.18)', zIndex: 1000, maxHeight: 240, overflowY: 'auto' }}>
                                        {addTaskDialog.options.map((job, idx) => (
                                            <button
                                                type="button"
                                                key={job.id}
                                                onClick={() => setAddTaskDialog(p => ({ ...p, job, value: job.idf_no || String(job.id), options: [] }))}
                                                onMouseEnter={() => setAddTaskDialog(p => ({ ...p, activeIdx: idx }))}
                                                style={{ display: 'block', width: '100%', padding: '6px 8px', textAlign: 'left', background: idx === addTaskDialog.activeIdx ? '#0d6efd' : 'transparent', color: idx === addTaskDialog.activeIdx ? '#fff' : '#222', border: 'none', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', fontSize: 12 }}
                                            >
                                                {job.idf_no || `JOB ${job.id}`} — {job.customer_name || '-'} — {job.vessel || '-'}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {addTaskDialog.job && (
                                <div style={{ marginTop: 10, padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12 }}>
                                    <div><strong>IDF:</strong> {addTaskDialog.job.idf_no || '-'}</div>
                                    <div><strong>Customer:</strong> {addTaskDialog.job.customer_name || '-'}</div>
                                    <div><strong>Shipping Line:</strong> {addTaskDialog.job.shipping_line || '-'}</div>
                                    <div><strong>Vessel:</strong> {addTaskDialog.job.vessel || '-'}</div>
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
                                <button
                                    className="add-btn"
                                    disabled={!addTaskDialog.job}
                                    onClick={() => createTaskRow(addTaskDialog.job)}
                                    style={{ width: 110, height: 36 }}
                                >
                                    Create Task
                                </button>
                                <button className="icon-btn cancel-btn" onClick={() => setAddTaskDialog(null)} style={{ width: 90, height: 36 }}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {renderTaskModal()}
            {renderConfigModal()}

            <ConfirmDialog
                open={generateConfirm}
                title="Generate Report"
                message="You are generating the report without remarks for some tasks. Are you sure you want to continue?"
                onConfirm={() => { setGenerateConfirm(false); generateReport(true); }}
                onCancel={() => setGenerateConfirm(false)}
            />

            <ConfirmDialog
                open={!!deleteTaskConfirm}
                title="Delete Task"
                message={deleteTaskConfirm ? `Delete task "${deleteTaskConfirm.task || '-'}"? This cannot be undone.` : ''}
                onConfirm={() => {
                    const row = deleteTaskConfirm;
                    setDeleteTaskConfirm(null);
                    if (row) deleteJobTask(row);
                }}
                onCancel={() => setDeleteTaskConfirm(null)}
            />

            <ConfirmDialog
                open={!!undoneTaskConfirm}
                title="Mark Task Not Done"
                message="Are you sure you would like to mark this task as NOT DONE?"
                onConfirm={() => {
                    const { job, task } = undoneTaskConfirm;
                    setUndoneTaskConfirm(null);
                    setDoneTasksModal(null);
                    saveTask(job, { ...task, done: false });
                }}
                onCancel={() => setUndoneTaskConfirm(null)}
            />

            <ConfirmDialog
                open={!!deleteReportConfirm}
                title="Delete Report"
                message={deleteReportConfirm ? `Delete "${deleteReportConfirm.title}"? This cannot be undone.` : ''}
                onConfirm={() => {
                    const r = deleteReportConfirm;
                    setDeleteReportConfirm(null);
                    if (r) deleteGeneratedReport(r);
                }}
                onCancel={() => setDeleteReportConfirm(null)}
            />
        </div>
    );
}
